// 2026-08-31 - FormuLearning - COMP-REFERENCE-LOT-PICKER (이슈 #58 S2/#65)
// 파일 위치: frontend/src/features/step4/ReferenceLotPicker.tsx
// 목적: Optimization Run 이 **목표로 삼을 대조약 Lot**을 run 시점에 고른다(BR-RND-04).
//       폐기된 `COMP-ACTIVE-LOT-BANNER`(활성 Lot 전환 드롭다운)의 대체다 — Step 4 의
//       대조약 축은 활성 Lot 추종이 아니라 **선택**이다(M84-04·33·56·70).
//
// 화면 규약 셋.
//   ① 조건이 다른 Lot 을 **목록에서 감추지 않는다** — 사유와 함께 비활성으로 둔다.
//      감추면 "왜 이 Lot 이 안 보이는지" 를 화면이 설명하지 못한다(BR-RND-08).
//   ② "측정 격자" 라는 말을 노출하지 않는다 — 고객이 쓰지 않는 용어다. 사용자 언어는
//      **"같은 시점·용액으로 측정된 Lot"** 이다(폐기된 BR-LOT-05-보 승계).
//   ③ 바꾸면 **f2 가 재계산됨을 안내**한다. 실제 재계산은 추천 실행 시점이다
//      (사용자 확정 260831) — 이미 실행된 라운드의 기준점을 사후에 갈아 끼우지 않는다.

import { useState } from 'react'
import { lotDisplayName, type ReferenceLotCandidate } from '../../api/lot'

interface Props {
  candidates: ReferenceLotCandidate[]
  /** 이번 run 이 고른 Lot. 미선택이면 서버가 활성 Lot 으로 폴백한다. */
  selectedId: string | null
  onSelect: (lotId: string) => void
  disabled?: boolean
}

function fmtDate(isoDate: string | null): string {
  return isoDate && /^\d{4}-\d{2}-\d{2}/.test(isoDate) ? isoDate.slice(0, 10) : ''
}

export function candidateLabel(c: ReferenceLotCandidate): string {
  const date = fmtDate(c.test_date)
  const base = `${lotDisplayName(c.label, c.lot_no)}${date ? ` · ${date}` : ''}`
  return c.selectable ? base : `${base} — ${c.reason ?? '선택할 수 없습니다'}`
}

export default function ReferenceLotPicker({ candidates, selectedId, onSelect, disabled }: Props) {
  const [open, setOpen] = useState(false)
  // 변경 안내는 **이번 화면에서 바꿨을 때만** 띄운다. 처음 그려질 때 뜨면 경고가 아니라 잡음이다.
  const [changed, setChanged] = useState(false)

  const selectable = candidates.filter((c) => c.selectable)
  const selected = candidates.find((c) => c.id === selectedId) ?? null

  return (
    <div className="mb-3" data-testid="reference-lot-picker">
      <div className="flex flex-wrap items-center gap-2">
        <h5 className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
          Reference Drug
        </h5>
        <span className="rounded px-1.5 py-0.5 text-[11px] font-semibold"
              style={{ background: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' }}
              data-testid="reference-lot-name">
          {selected ? lotDisplayName(selected.label, selected.lot_no) : '미선택 (활성 Lot 사용)'}
        </span>
        {!disabled && selectable.length > 0 && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="change-reference-drug"
            className="rounded border px-2 py-0.5 text-[10px] font-semibold text-text-body transition-opacity hover:opacity-90"
            style={{ borderColor: 'var(--border-purple)', backgroundColor: 'var(--bg-chip-purple)' }}
          >
            Change Reference Drug
          </button>
        )}
      </div>

      {/* 동등성 문구(M84-33) — 이 run 이 무엇을 목표로 하는지 헤더가 말한다 */}
      <p className="mt-1 text-[10px] text-text-muted" data-testid="equivalence-note">
        이 대조약과의 동등성(f2 ≥ 50)을 목표로 최적화합니다.
      </p>

      {open && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select
            value={selectedId ?? ''}
            onChange={(e) => {
              if (!e.target.value) return
              onSelect(e.target.value)
              setChanged(true)
            }}
            disabled={disabled}
            data-testid="reference-lot-select"
            className="rounded bg-bg-input px-1.5 py-0.5 text-[11px] outline-none"
            style={{ color: 'var(--color-accent-teal)', border: '1px solid var(--border-soft)' }}
          >
            {selectedId === null && <option value="">선택하십시오</option>}
            {candidates.map((c) => (
              // 조건 불일치는 `disabled` 로 남긴다 — 목록에서 빼지 않는다(BR-RND-08)
              <option key={c.id} value={c.id} disabled={!c.selectable}>
                {candidateLabel(c)}
              </option>
            ))}
          </select>
          <span className="text-[10px] text-text-muted">
            같은 시점·용액으로 측정된 Lot만 고를 수 있습니다.
          </span>
        </div>
      )}

      {selectable.length === 0 && (
        <p
          className="mt-2 rounded border px-3 py-2 text-[10px] leading-relaxed text-text-secondary"
          data-testid="no-selectable-lot"
          style={{ borderColor: 'var(--border-purple)', backgroundColor: 'var(--bg-chip-purple)' }}
        >
          ⚠ 선택할 수 있는 대조약 Lot이 없습니다. 같은 시점·용액으로 측정된 Lot이 있어야
          용출 프로파일을 비교할 수 있습니다.
        </p>
      )}

      {changed && (
        <p
          className="mt-2 rounded border px-3 py-2 text-[10px] text-text-body"
          data-testid="f2-recalc-notice"
          style={{ borderColor: 'var(--border-teal)', backgroundColor: 'var(--bg-chip-teal)' }}
        >
          대조약을 바꿨습니다 — 추천을 실행하면 f2가 재계산됩니다.
        </p>
      )}
    </div>
  )
}
